import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { globalGetService } from "../../../utils/globalApiServices";
import { checkApiStatus } from "../../../utils";
import { getReserveRateEscalationApi } from "../apiService";
import EscalationModal from "../../reserve_rate/components/EscalationModal";
import "../../reserve_rate/styles/reserverate.scss";

// EscalationModal picks its layout from the reserve rate item ({ value } of reserve_rate_type / reserve_rate_based_on);
// the analytics row only sends their labels, so map them back to the values.
const RESERVE_TYPE_VALUES = { "fixed": 1, "pbh/flexi": 2, "ratio/derate": 3 };
// "ratio/derate" (11) is deliberately left out: the pivot layout needs a `ratios` array, which shape inference already detects
const RATIO_DERATE_BASED_ON_VALUES = { "range base fh/fc ratio": 8, "fh/fc ratio": 9, "derate": 10 };

const normalizeLabel = (label) => String(label || "").trim().toLowerCase();

// The escalation list shape decides the ratio/derate layout; the based-on label is only a fallback
function inferRatioDerateBasedOn(list) {
  const first = Array.isArray(list) ? list[0] : null;
  if (!first) return null;
  if (Array.isArray(first.ratios)) return 11;
  if (first.range_label !== undefined) return 8;
  if (first.ratio !== undefined) return 9;
  if (first.derate !== undefined) return 10;
  return null;
}

// A flat list (single rate, no range/ratio/derate rows) is a fixed rate within a PBH/Flexi reserve
function isFlatRateList(list) {
  const first = Array.isArray(list) ? list[0] : null;
  if (!first) return false;
  return ["range_label", "ratio", "ratios", "derate", "type"].every((key) => first[key] === undefined);
}

function buildReserveRateItem(row, list) {
  const typeValue = RESERVE_TYPE_VALUES[normalizeLabel(row?.reserveType)] ?? null;
  let basedOnValue = null;
  let fixedRate = false;
  if (typeValue === 3) {
    basedOnValue = inferRatioDerateBasedOn(list) ?? RATIO_DERATE_BASED_ON_VALUES[normalizeLabel(row?.reserveRateBasedOn)] ?? null;
  } else if (isFlatRateList(list)) {
    // EscalationModal treats based-on 4 + fixed_rate as the fixed rental layout
    basedOnValue = 4;
    fixedRate = true;
  }
  return {
    reserve_rate_type: typeValue ? { value: typeValue, label: row?.reserveType } : null,
    reserve_rate_based_on: basedOnValue ? { value: basedOnValue, label: row?.reserveRateBasedOn } : null,
    fixed_rate: fixedRate,
  };
}

// Guard the shared EscalationModal (it assumes well-formed arrays) against malformed list entries
const toArray = (value) => (Array.isArray(value) ? value.filter((entry) => entry && typeof entry === "object") : []);

function sanitizeEscalationList(list) {
  const entries = toArray(list);
  // the derate pivot reads `ratios` on every row, so once any row has it all rows get an array
  const hasRatios = entries.some((entry) => entry.ratios !== undefined);
  return entries.map((entry) => ({
    ...entry,
    escalations: toArray(entry.escalations),
    ...(hasRatios ? { ratios: toArray(entry.ratios).map((ratio) => ({ ...ratio, escalations: toArray(ratio.escalations) })) } : {}),
  }));
}

// Opened from the "Upcoming Escalation Events" table for non-fixed reserve rates
const EscalationDetailsDialog = ({ row, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [escalationData, setEscalationData] = useState({ data: null, item: null });

  useEffect(() => {
    if (!row?.leaseSlug || !row?.reserveRateSlug) return undefined;
    let cancelled = false;
    setLoading(true);
    setEscalationData({ data: null, item: null });
    globalGetService(getReserveRateEscalationApi(row.leaseSlug, row.reserveRateSlug))
      .then((response) => {
        if (cancelled) return;
        if (checkApiStatus(response)) {
          const list = sanitizeEscalationList(response?.data?.data?.list);
          setEscalationData({ data: { ...response?.data?.data, list }, item: buildReserveRateItem(row, list) });
        } else {
          enqueueSnackbar(response?.data?.message || "Unable to fetch escalation details", { variant: "error", anchorOrigin: { vertical: "top", horizontal: "right" } });
        }
      })
      .catch(() => {
        if (!cancelled) enqueueSnackbar("Unable to fetch escalation details", { variant: "error", anchorOrigin: { vertical: "top", horizontal: "right" } });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [row?.leaseSlug, row?.reserveRateSlug, enqueueSnackbar]);

  if (!row) return null;

  return (
    <EscalationModal
      escalationData={escalationData}
      onToggleModal={onClose}
      handleItemClicked={() => {}}
      skeletonLoader={loading}
    />
  );
};

export default EscalationDetailsDialog;
